// Base API configuration and utilities
import { getApiUrl } from '@/config/runtime-env'
import { getAccessToken, getTokens, saveTokens, clearTokens, type Tokens } from '@/utils/tokenService'
import { API_ENDPOINTS } from '@/utils/constants'

const API_BASE_URL = getApiUrl()

class ApiError extends Error {
  constructor(public status: number, message: string, public data?: any) {
    super(message)
    this.name = 'ApiError'
  }
}

// Refresh lock to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<Tokens> | null = null

// Emit custom events for refresh state
function emitRefreshEvent(isRefreshing: boolean) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('tokenRefreshing', { 
      detail: { isRefreshing } 
    }))
  }
}

// Refresh tokens function
async function refreshTokens(): Promise<Tokens> {
  // If already refreshing, return the existing promise
  if (refreshPromise) {
    return refreshPromise
  }

  const current = getTokens()
  if (!current?.refreshToken) {
    throw new Error("No refresh token available")
  }

  // Emit event that refresh is starting
  emitRefreshEvent(true)

  // Create the refresh promise
  refreshPromise = (async () => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken: current.refreshToken })
      })

      if (!response.ok) {
        throw new Error("Refresh token invalid or expired")
      }

      const responseData = await response.json()
      const data = responseData.data || responseData
      
      if (!data.accessToken) {
        throw new Error("No access token in refresh response")
      }

      const newTokens: Tokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || current.refreshToken // Use new refresh token if provided
      }
      saveTokens(newTokens)
      return newTokens
    } catch (error) {
      // Only clear tokens if it's a definitive auth failure
      if (error instanceof Error && 
          (error.message.includes("invalid") || 
           error.message.includes("expired"))) {
        clearTokens()
      }
      throw error
    } finally {
      // Clear the refresh promise after completion
      refreshPromise = null
      // Emit event that refresh is complete
      emitRefreshEvent(false)
    }
  })()

  return refreshPromise
}

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return getAccessToken()
}

// Create headers with auth token
const createHeaders = (additionalHeaders?: HeadersInit): HeadersInit => {
  const token = getAuthToken()
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...additionalHeaders
  }
}

// Base fetch wrapper with error handling and automatic token refresh
export const apiFetch = async <T>(
  endpoint: string,
  options: RequestInit & { retryCount?: number } = {}
): Promise<any> => {
  const url = `${API_BASE_URL}${endpoint}`
  const retryCount = options.retryCount || 0

  const config: RequestInit = {
    ...options,
    headers: createHeaders(options.headers)
  }

  try {
    const response = await fetch(url, config)

    if (!response.ok) {
      // Handle 401 errors with token refresh
      if (response.status === 401 && retryCount === 0) {
        console.log('[apiFetch] Got 401, attempting token refresh...')
        try {
          await refreshTokens()
          console.log('[apiFetch] Token refreshed successfully, retrying request...')
          // Retry with new token
          return apiFetch<T>(endpoint, { ...options, retryCount: 1 })
        } catch (refreshError) {
          console.error('[apiFetch] Token refresh failed:', refreshError)
          // Only throw auth error if refresh truly failed
          if (refreshError instanceof Error && 
              (refreshError.message.includes("invalid") || 
               refreshError.message.includes("expired"))) {
            const errorResponse = await response.json().catch(() => ({}))
            const errorData = errorResponse.data || errorResponse
            throw new ApiError(
              response.status,
              errorData.message || 'Authentication failed',
              errorData
            )
          }
          // For network errors during refresh, continue with original error
        }
      }
      
      const errorResponse = await response.json().catch(() => ({}))
      const errorData = errorResponse.data || errorResponse
      throw new ApiError(
        response.status,
        errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        errorData
      )
    }

    // Handle responses with no content (like 204 No Content from DELETE)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return null
    }

    const data = await response.json()
    if (data?.meta) {
      return {
        data: data.data,
        meta: data.meta
      }
    } else {
      // Nullish coalescing, not ||: a legitimately-null payload (e.g. "no
      // draft saved yet") must stay null, not fall through to the whole
      // envelope just because null is falsy.
      return 'data' in (data ?? {}) ? data.data : data
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    throw new ApiError(0, 'Network error or invalid response', error)
  }
}

// HTTP method helpers
export const api = {
  get: <T>(endpoint: string, params?: Record<string, any>) => {
    const url = params ? `${endpoint}?${new URLSearchParams(params)}` : endpoint
    return apiFetch<T>(url, { method: 'GET' })
  },

  post: <T>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }),

  patch: <T>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    }),

  put: <T>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }),

  delete: <T>(endpoint: string) =>
    apiFetch<T>(endpoint, { method: 'DELETE' })
}

export { ApiError }